import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Content, DateInputControl, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { UserSelectControl } from '../Nomen/User/UserSelectControl';
import { ResearchCenterSchema } from './model/ResearchCenterSchema';

interface ResearchCenterFormProps extends FormSubmit<ResearchCenterSchema> {
  title: string;
}

export function ResearchCenterForm(props: ResearchCenterFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<ResearchCenterSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<ResearchCenterSchema>
              name="name"
              label={t('researchCenters.form.fields.name')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <TextInputControl<ResearchCenterSchema>
              name="description"
              label={t('researchCenters.form.fields.description')}
              isSubmitting={isSubmitting}
            />
            <DateInputControl<ResearchCenterSchema>
              name="creationDate"
              label={t('researchCenters.form.fields.creationDate')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <UserSelectControl<ResearchCenterSchema>
              name="director"
              label={t('researchCenters.form.fields.director')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <UserSelectControl<ResearchCenterSchema>
              label={t('researchCenters.form.fields.sciDirector')}
              name="sciDirector"
              isSubmitting={isSubmitting}
            />
            <TextInputControl<ResearchCenterSchema>
              name="researchDomain"
              label={t('researchCenters.form.fields.researchDomain')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="doctoralStudentsNo"
              label={t('researchCenters.form.fields.doctoralStudentsNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="personsWithDoctorDegreeNo"
              label={t('researchCenters.form.fields.personsWithDoctorDegreeNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="professorsNo"
              label={t('researchCenters.form.fields.professorsNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="associateProfessorsNo"
              label={t('researchCenters.form.fields.associateProfessorsNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="managersTotalNo"
              label={t('researchCenters.form.fields.managersTotalNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="assistantProfessorsNo"
              label={t('researchCenters.form.fields.assistantProfessorsNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="researchersINo"
              label={t('researchCenters.form.fields.researchersINo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="researchersIINo"
              label={t('researchCenters.form.fields.researchersIINo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="researchersIIINo"
              label={t('researchCenters.form.fields.researchersIIINo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="scientificResearchersNo"
              label={t('researchCenters.form.fields.scientificResearchersNo')}
              isSubmitting={isSubmitting}
            />
            <NumberInputControl<ResearchCenterSchema>
              name="researchAssistantsNo"
              label={t('researchCenters.form.fields.researchAssistantsNo')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<ResearchCenterSchema>
              name="researchInfrastructure"
              label={t('researchCenters.form.fields.researchInfrastructure')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<ResearchCenterSchema>
              name="activityVisibility"
              label={t('researchCenters.form.fields.activityVisibility')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<ResearchCenterSchema>
              name="researchAchievements"
              label={t('researchCenters.form.fields.researchAchievements')}
              isSubmitting={isSubmitting}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
